<template>
  <div>

    <info-hint v-if="!scales.length" variant="neutral" icon="icon-bulb-63">
      No rating scales yet. <router-link to="/games/settings?tab=ratings">Add one</router-link>
    </info-hint>

    <template v-else>
      <p v-if="!attached.length" class="text-muted small mb-2">This event has no ratings.</p>

      <div v-for="item in attached" :key="item.scale.id" class="row mb-2">
        <div class="col-lg-3">
          <base-input :label="item.scale.name" :error="errors[item.scale.id]">
            <rating-input
              :value="item.rating.value"
              :scale="item.scale"
              :icon="item.rating.icon"
              :color="item.rating.color"
              @input="update(item.scale.id, { value: $event })"
            />
          </base-input>
        </div>
        <div class="col-lg-3 d-flex align-items-center">
          <div>
            <el-switch
              active-color="#e14eca"
              :value="item.rating.show_to_customers"
              @change="update(item.scale.id, { show_to_customers: $event })"
            />
            <label class="text-white mb-0"> &nbsp; Show to customers
              <el-popover trigger="hover" placement="right">
                <div>
                  <div class="popover-body">Shows this rating on your booking site and in emails.</div>
                </div>
                <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
              </el-popover>
            </label>
          </div>
        </div>
        <div class="col-lg-5 d-flex align-items-center">
          <span v-if="hasOwnLook(item.rating)" class="small text-muted">
            Shown with this event's own icon and color instead of the scale's.
            <a href="#" class="ml-1" @click.prevent="update(item.scale.id, { icon: null, color: null })">Use scale look</a>
          </span>
        </div>
        <div class="col-lg-1 d-flex align-items-center justify-content-end">
          <button
            type="button"
            class="row-remove"
            :title="'Remove ' + item.scale.name"
            :aria-label="'Remove ' + item.scale.name"
            @click="remove(item.scale.id)"
          >
            <i class="tim-icons icon-simple-remove"></i>
          </button>
        </div>

      </div>

      <div class="d-flex align-items-center mt-2">
        <span v-if="atLimit" class="small text-muted mr-3">An event can have up to {{ MAX_RATINGS_PER_EVENT }} ratings.</span>
        <el-dropdown v-else-if="available.length" trigger="click" class="mr-3" @command="add">
          <base-button variant="outlined" tone="primary" size="sm">
            <i class="tim-icons icon-simple-add"></i> Add rating
          </base-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-for="scale in available" :key="scale.id" :command="scale.id">{{ scale.name }}</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <router-link to="/games/settings?tab=ratings" class="small">Manage rating scales</router-link>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Dropdown, DropdownItem, DropdownMenu } from 'element-ui'
import { MAX_RATINGS_PER_EVENT } from '@/util/ratings'
import type { GameRating, RatingScale } from '@/util/ratings'

// Ratings section body of the event editor (EditGame renders the section frame and title). Lists only the scales this event is rated on; "Add rating"
// attaches another active scale and the X removes one. Every rating uses its scale's look. v-model is the attached ratings only; the
// event save sends every active scale and the lambda deletes the ones that are not attached.
const props = defineProps({
  value: { type: Array as () => GameRating[], default: () => [] },
  scales: { type: Array as () => RatingScale[], default: () => [] },
  // Save errors from the event editor, keyed by scale id.
  errors: { type: Object as () => Record<number, string>, default: () => ({}) },
})

const emit = defineEmits(['input'])

// Element components used in the template that are not globally registered.
const ElDropdown = Dropdown
const ElDropdownMenu = DropdownMenu
const ElDropdownItem = DropdownItem


// Attached ratings in scale order, each paired with its scale. Ratings on archived scales are not listed.
const attached = computed(() => props.scales
  .map(scale => ({ scale, rating: props.value.find(r => r.rating_scale_id === scale.id) }))
  .filter((item): item is { scale: RatingScale, rating: GameRating } => !!item.rating))

const available = computed(() => props.scales.filter(scale => !props.value.some(r => r.rating_scale_id === scale.id)))

const atLimit = computed(() => attached.value.length >= MAX_RATINGS_PER_EVENT)

// A rating's name is always its scale's name. Per-event icons and colors only come from migrated
// events (venues that styled each event differently before scales existed). The editor never sets
// one; it can only clear it back to the scale's look.
function hasOwnLook(rating: GameRating) {
  return !!(rating.icon || rating.color)
}

// An icons rating starts at 0, so an attached rating always has a value and is always saved.
// A named-levels rating has no zero: it starts empty and the event editor asks for a level on save.
function add(scaleId: number) {
  const scale = props.scales.find(s => s.id === scaleId)
  const value = scale && scale.display_type === 'labels' ? null : 0
  emit('input', [...props.value, { rating_scale_id: scaleId, value, label: null, icon: null, color: null, show_to_customers: true }])
}

function remove(scaleId: number) {
  emit('input', props.value.filter(r => r.rating_scale_id !== scaleId))
}

function update(scaleId: number, patch: Partial<GameRating>) {
  emit('input', props.value.map(r => (r.rating_scale_id === scaleId ? { ...r, ...patch } : r)))
}
</script>
